<template>
  <div class="login_form_cntainer d-flex-column" :class="[version]">
    <div class="d-flex-column flex-9 forms_container gap-30">
      <div class="d-flex-column">
        <h3 class="t-white title-h3 font-family-1">Log In</h3>
        <p class="t-white regular-xl font-family-2 text-center">
          To access the website, enter a valid email address.
        </p>
      </div>
      <transition name="slider-left" mode="out-in">
        <form
          v-if="params.step === 0"
          @submit.prevent="doLogin(params)"
          class="login_form d-flex-column"
        >
          <input-field
            icon-end="EmailIcon"
            type="email"
            :placeholder="$t('login.email_adress')"
            v-model="params.email"
          />
          <button
            class="full-width primary filled b-radius-6 xl font-family-1 body-l f-w-600 height-48"
            :class="[isLoading ? 'loading' : '']"
            :disabled="cloudflareCaptcha.length <= 0"
          >
            {{ $t("login.continue_with_email") }}
          </button>
          <div class="d-flex-row" v-if="params.step === 0"></div>
        </form>

        <div class="login_form d-flex-column" style="width: 100%" v-else>
          <otp-form
            :email="params.email"
            :doLogin="doLogin"
            :captcha-token="cloudflareCaptcha"
            :go-back="goBack"
          />
        </div>
      </transition>
      <ClientOnly>
        <NuxtTurnstile
          v-model="cloudflareCaptcha"
          :site-key="publicConfig.turnstileSiteKey"
          ref="turnstile"
          v-if="params.step === 0"
        />
      </ClientOnly>
      <div class="d-flex-row gap-4 height-max-content">
        <p class="gap-2 font-family-2 t-light-orange medium-m">
          {{ $t("trouble_login") }}
        </p>
        <button
          class="gap-2 font-family-2 t-orange medium-m"
          @click="handleTidio"
        >
          <span>{{ $t("get_help") }}</span>
        </button>
      </div>
      <div class="login_form d-flex-column">
        <divider :show-or="true" />
      </div>
      <div
        class="d-flex-row space-between align-start login_form flex-1 height-max-content"
      >
        <a
          v-if="googleLink !== null"
          :href="googleLink"
          @click="set"
          class="b-radius-12 full-width height-48 google_btn flex-5 large"
        >
          <GoogleFilled />
        </a>
        <a
          v-if="discordLink !== null"
          :href="discordLink"
          @click="setDiscord"
          class="b-radius-12 full-width height-48 discord_btn flex-5 large"
        >
          <DiscordNegative />
        </a>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from "vue";
import InputField from "~/components/custom/InputField.vue";
import OtpForm from "~/components/forms/OtpForm.vue";
import GoogleFilled from "../icons/socials/GoogleFilled.vue";
import Divider from "../custom/Divider.vue";
import DiscordNegative from "../icons/socials/DiscordNegative.vue";

// HOOKS
const { setSnack } = useGenericStore();
const { setUser, getUtm } = useUserStore();
const localePath = useLocalePath();
const { public: publicConfig } = useRuntimeConfig();
const router = useRouter();
const { getLocale } = useFunctions();
const route = router.currentRoute;
const discordLink = ref(null);
const googleLink = ref(null);
const { initializeCrisp, isCrispLoaded, openChat, setUserDataToCrisp } =
  useCrispComposable();
// REACTIVE VARIABLES
const params = ref({
  step: 0,
  email: "",
  token: "",
});
const goBack = () => {
  params.value.step = 0;
};
const cloudflareCaptcha = ref("");
const isLoading = ref(false);
const props = defineProps({
  continueCheckout: {
    default: () => {},
  },
  version: {
    default: "",
  },
  redirect: {
    default: "login",
  },
});

const turnstile = ref(null);
const failedAttemps = ref(0);
const saveLocale = () => {
  window.localStorage.setItem("AUTH_LOCALE", getLocale());
};
const reloadTurnstile = () => {
  if (turnstile.value) {
    cloudflareCaptcha.value = "";
    turnstile.value?.reset();
  }
};
const set = () => {
  window.localStorage.setItem("GOOGLE_REDIRECT", route.value.href);
  saveLocale();
};
const setDiscord = () => {
  window.localStorage.setItem("DISCORD_REDIRECT", route.value.href);
  saveLocale();
};
const fetchGoogleLink = async () => {
  try {
    const response = await useApiPost("/api/auth/get-google-link", {
      method: "POST",
      body: {
        locale: getLocale(),
        redirect: "login",
      },
    });
    if (response) {
      googleLink.value = response;
    }
  } catch (error) {
    console.log(error);
  }
};
const fetchDiscordLink = async () => {
  try {
    const response = await useApiPost("/api/auth/get-discord-link", {
      method: "POST",
      body: {
        locale: getLocale(),
        redirect: "login",
      },
    });
    if (response) {
      // googleLink.value = response;
      discordLink.value = response;
    }
  } catch (error) {
    console.log(error);
  }
};

fetchGoogleLink();
fetchDiscordLink();
// METHODS
const doLogin = async (passedParams) => {
  try {
    const visitorId = window.localStorage.getItem("VISITOR_ID") || null;

    isLoading.value = true;
    // await useApiPost("/sanctum/csrf-cookie");

    if (params.value.step === 0) {
      failedAttemps.value = 0;
    }
    let additionalParams = {};
    additionalParams = {
      cloudflareCaptcha:
        params.value.step === 0 ? cloudflareCaptcha.value : null,
      referral: getToken("referral"),
    };
    const mappedParams = paramsApi({
      params: {
        ...passedParams,
        ...additionalParams,
      },
    });
    const { data: response } = await useApiPost("/api/auth/login", {
      method: "post",
      body: {
        ...mappedParams,
        visitor_id: visitorId,
      },
    });

    if (response?.status === 422) {
      failedAttemps.value += 1;
      if (failedAttemps.value === 5) {
        params.value.step = 0;
      }
    }

    if (response.message_title) {
      setSnack({
        title: response.message_title,
        status: response.message_status,
        description: response.message,
        showProgress: true,
        position: "top-right",
        type: response.message_status,
        timeout: 5000,
      });
    }

    isLoading.value = false;
    if (response.params) {
      params.value = response.params;
    }
    if (response?.success && response?.user) {
      setUser(response.user);
      if (isCrispLoaded()) {
        setUserDataToCrisp({
          id: response.user?.id,
          email: response?.user?.email,
          username: response?.user?.username,
          avatar: response?.user?.avatar,
          tags: response?.user?.order_invoice_codes,
        });
      }
      props.continueCheckout();
    }
  } catch (error) {
    isLoading.value = false;
    console.log(error);
  }
};

const redirectUser = (url) => {
  window.location.href = url;
};

const authGoogle = async (googleCode) => {
  try {
    const visitorId = window.localStorage.getItem("VISITOR_ID") || null;

    isLoading.value = true;
    // await useApiPost("/sanctum/csrf-cookie");
    const { data: authGoogleResponse } = await useApiPost(
      "/api/auth/auth-google",
      {
        method: "POST",
        body: {
          locale: getLocale(),
          referral: getToken("referral"),
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          googleCode: googleCode,
          visitor_id: visitorId,
        },
      }
    );
    if (authGoogleResponse.redirect && authGoogleResponse.success === true) {
      const utmDataSet = getUtm;

      await window?.analytics?.track(
        "User do " + authGoogleResponse?.dataLayerSource,
        {
          ...utmDataSet,
        }
      );
      fetchUser();
      fetchUserGames();
      // router.push(localePath("/"));
      props.continueCheckout();
    } else {
      setSnack({
        title: authGoogleResponse.message_title,
        status: authGoogleResponse.message_status,
        description: authGoogleResponse.message,
        showProgress: true,
        position: "top-right",
        type: authGoogleResponse.message_status,
        timeout: 5000,
      });
      isLoading.value = false;
    }
  } catch (error) {
    console.log(error);
  }
};

const authDiscord = async (googleCode) => {
  try {
    const visitorId = window.localStorage.getItem("VISITOR_ID") || null;

    isLoading.value = true;
    // await useApiPost("/sanctum/csrf-cookie");
    const { data: authDiscordResponse } = await useApiPost(
      "/api/auth/auth-discord",
      {
        method: "POST",
        body: {
          locale: getLocale(),
          referral: getToken("referral"),
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          discordCode: googleCode,
          visitor_id: visitorId,
        },
      }
    );
    if (authDiscordResponse.redirect && authDiscordResponse.success === true) {
      const utmDataSet = getUtm;

      await window?.analytics?.track(
        "User do " + authDiscordResponse?.dataLayerSource,
        {
          ...utmDataSet,
        }
      );
      fetchUser();
      fetchUserGames();
      props.continueCheckout();

      // router.push(localePath("/"));
    } else {
      setSnack({
        title: authDiscordResponse.message_title,
        status: authDiscordResponse.message_status,
        description: authDiscordResponse.message,
        showProgress: true,
        position: "top-right",
        type: authDiscordResponse.message_status,
        timeout: 5000,
      });
      isLoading.value = false;
    }
  } catch (error) {
    console.log(error);
  }
};

onNuxtReady(() => {
  if (route.value.query.code && route.value.query.state === "google") {
    authGoogle(route.value.query.code);
  }

  if (route.value.query.code && route.value.query.state === "discord") {
    authDiscord(route.value.query.code);
  }

  if (route.value.query.referral) {
    saveToken(route.value.query.referral, "referral");
  }
});

watch(params, (newVal) => {
  if (newVal.step === 0) {
    reloadTurnstile();
  }
});

const handleTidio = () => {
  if (isCrispLoaded()) {
    openChat();
  } else {
    initializeCrisp();
    openChat();
  }
};
</script>
